import { useState } from 'react'

import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'

import { resolveMediaUrl } from '../media'

type AvatarSize = 'sm' | 'md' | 'lg' | 'profile' | 'xl'

const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'size-10',
  md: 'size-11',
  lg: 'size-20',
  profile: 'size-[84px]',
  xl: 'size-[88px] min-[800px]:size-[128px]',
}

const INITIAL_CLASSES: Record<AvatarSize, string> = {
  sm: 'text-base',
  md: 'text-lg',
  lg: 'text-2xl',
  profile: 'text-[32px]',
  xl: 'text-[34px] min-[800px]:text-5xl',
}

interface UserAvatarProps {
  name: string
  src: string | null | undefined
  size?: AvatarSize
  className?: string
}

/**
 * Avatar with an initials fallback and a broken-image fallback. The name is
 * always rendered next to it in the surrounding card, so the image is
 * decorative (`alt=""`) and the initials are not duplicated copy.
 */
export function UserAvatar({
  name,
  src,
  size = 'md',
  className = '',
}: UserAvatarProps) {
  const resolved = resolveMediaUrl(src)
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const failed = Boolean(resolved && failedSrc === resolved)
  const initial = name.trim().charAt(0).toLocaleUpperCase()
  const AvatarIcon = KuniriIcons.user
  const iconSize = size === 'xl' ? 34 : size === 'profile' ? 32 : size === 'lg' ? 30 : 20

  return (
    <span
      className={`relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-mint text-teal ${SIZE_CLASSES[size]} ${className}`}
      aria-hidden="true"
    >
      {initial ? (
        <span className={`font-display font-extrabold ${INITIAL_CLASSES[size]}`}>
          {initial}
        </span>
      ) : (
        <AvatarIcon size={iconSize} weight={kuniriIconWeight} />
      )}
      {resolved && !failed ? (
        <img
          src={resolved}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="absolute inset-0 size-full object-cover"
          onError={() => setFailedSrc(resolved)}
        />
      ) : null}
    </span>
  )
}
